/**
 * TextToASTConverter - Integration layer for ClientFirst ↔ Backend AST conversion
 * 
 * Converts ClientFirst text-based format to backend-compatible hierarchical AST
 * and vice versa, enabling seamless integration without modifying ClientFirst core.
 */

export interface ConversionOptions {
  documentType?: string;
  jurisdiction?: string;
  preserveFormatting?: boolean;
}

export interface BackendAST {
  blocks: BackendBlock[];
}

export interface BackendBlock {
  id: string;
  type: 'Paragraph' | 'Heading' | 'List' | 'ListItem' | 'Image' | 'Table' | 'CodeBlock' | 'Quote' | 'Section' | 'Clause' | 'PageBreak' | 'SectionBreak';
  styleId?: string;
  attrs?: Record<string, any>;
  children?: BackendRun[];
}

interface ConversionMetadata {
  originalSeparators?: string[];
  paragraphCount?: number;
}

export interface BackendRun {
  id: string;
  text: string;
  charStyleId?: string; // Optional - only set if using character styles
}

export interface ConversionResult {
  success: boolean;
  ast?: BackendAST;
  text?: string;
  errors?: string[];
  warnings?: string[];
  metadata?: ConversionMetadata;
}

export class TextToASTConverter {
  /**
   * Convert ClientFirst text to backend-compatible AST
   */
  convertTextToAST(text: string, options: ConversionOptions = {}): ConversionResult {
    try {
      // Handle empty text
      if (!text || text.trim().length === 0) {
        return {
          success: true,
          ast: { blocks: [] },
          warnings: ['Empty document converted to empty AST'],
          metadata: { originalSeparators: [], paragraphCount: 0 }
        };
      }

      // Split text into paragraphs and capture separators
      const { paragraphs, separators } = this.splitIntoParagraphsWithSeparators(text);
      
      if (paragraphs.length === 0) {
        return {
          success: true,
          ast: { blocks: [] },
          warnings: ['No content paragraphs found'],
          metadata: { originalSeparators: [], paragraphCount: 0 }
        };
      }

      // Convert paragraphs to AST blocks
      const blocks: BackendBlock[] = paragraphs.map((paragraph, index) => ({
        id: `B${index + 1}`,
        type: this.determineBlockType(paragraph, options),
        styleId: this.determineStyleId(paragraph, options),
        attrs: this.generateBlockAttributes(paragraph, options),
        children: [{
          id: `R${index + 1}`,
          text: paragraph.trim()
          // No charStyleId - using empty characterStyles in backend request
        }]
      }));

      const ast: BackendAST = { blocks };

      return {
        success: true,
        ast,
        warnings: this.generateConversionWarnings(text, ast, options),
        metadata: { 
          originalSeparators: separators,
          paragraphCount: paragraphs.length
        }
      };

    } catch (error) {
      return {
        success: false,
        errors: [`Text to AST conversion failed: ${error instanceof Error ? error.message : String(error)}`]
      };
    }
  }

  /**
   * Convert formatted text with styles to backend-compatible AST
   */
  convertFormattedTextToAST(text: string, styles: any[], options: ConversionOptions = {}): ConversionResult {
    try {
      // Handle empty text
      if (!text || text.trim().length === 0) {
        return {
          success: true,
          ast: { blocks: [] },
          warnings: ['Empty formatted document converted to empty AST'],
          metadata: { originalSeparators: [], paragraphCount: 0 }
        };
      }

      // Split text into paragraphs and capture separators
      const { paragraphs, separators } = this.splitIntoParagraphsWithSeparators(text);
      
      if (paragraphs.length === 0) {
        return {
          success: true,
          ast: { blocks: [] },
          warnings: ['No content paragraphs found'],
          metadata: { originalSeparators: [], paragraphCount: 0 }
        };
      }

      // Convert paragraphs to AST blocks (character styles will be handled separately)
      const blocks: BackendBlock[] = paragraphs.map((paragraph, index) => {
        return {
          id: `B${index + 1}`,
          type: this.determineBlockType(paragraph, options),
          styleId: this.determineStyleId(paragraph, options),
          attrs: this.generateBlockAttributes(paragraph, options),
          children: [{
            id: `R${index + 1}`,
            text: paragraph.trim()
            // Note: Character styles will be implemented in a future update
            // The backend expects charStyleId (string reference) not charStyles (array)
          }]
        };
      });

      const ast: BackendAST = { blocks };

      return {
        success: true,
        ast,
        warnings: this.generateConversionWarnings(text, ast, options),
        metadata: { 
          originalSeparators: separators,
          paragraphCount: paragraphs.length
        }
      };

    } catch (error) {
      return {
        success: false,
        errors: [`Formatted text to AST conversion failed: ${error instanceof Error ? error.message : String(error)}`]
      };
    }
  }


  /**
   * Convert backend AST to ClientFirst text
   */
  convertASTToText(ast: BackendAST, metadata?: ConversionMetadata): ConversionResult {
    try {
      if (!ast || !ast.blocks || ast.blocks.length === 0) {
        return {
          success: true,
          text: '',
          warnings: ['Empty AST converted to empty text']
        };
      }

      // Extract text from each block
      const paragraphs: string[] = ast.blocks.map(block => {
        if (block.children && block.children.length > 0) {
          // Concatenate all runs in the block
          return block.children
            .map(run => run.text || '')
            .join('')
            .trim();
        }
        return '';
      }).filter(paragraph => paragraph.length > 0);

      // Use original separators if available, otherwise default to double newlines
      let text: string;
      if (metadata?.originalSeparators && metadata.originalSeparators.length === paragraphs.length - 1) {
        // Reconstruct text with original separators
        text = paragraphs.reduce((result, paragraph, index) => {
          if (index === 0) return paragraph;
          const separator = metadata.originalSeparators![index - 1] || '\n\n';
          return result + separator + paragraph;
        }, '');
      } else {
        // Fallback to double newlines
        text = paragraphs.join('\n\n');
      }

      return {
        success: true,
        text,
        warnings: this.generateReverseConversionWarnings(ast, text)
      };

    } catch (error) {
      return {
        success: false,
        errors: [`AST to text conversion failed: ${error instanceof Error ? error.message : String(error)}`]
      };
    }
  }

  /**
   * Validate round-trip conversion accuracy
   */
  validateRoundTrip(originalText: string, options: ConversionOptions = {}): {
    success: boolean;
    accuracy: number;
    originalText: string;
    convertedText: string;
    differences?: string[];
  } {
    // Convert text → AST → text
    const astResult = this.convertTextToAST(originalText, options);
    if (!astResult.success || !astResult.ast) {
      return {
        success: false,
        accuracy: 0,
        originalText,
        convertedText: '',
        differences: ['AST conversion failed']
      };
    }

    const textResult = this.convertASTToText(astResult.ast, astResult.metadata);
    if (!textResult.success || textResult.text === undefined) {
      return {
        success: false,
        accuracy: 0,
        originalText,
        convertedText: '',
        differences: ['Text conversion failed']
      };
    }

    // Calculate accuracy
    const convertedText = textResult.text;
    const normalizedOriginal = this.normalizeText(originalText);
    const normalizedConverted = this.normalizeText(convertedText);
    
    const accuracy = this.calculateTextSimilarity(normalizedOriginal, normalizedConverted);
    const differences = this.findTextDifferences(normalizedOriginal, normalizedConverted);

    return {
      success: true,
      accuracy,
      originalText,
      convertedText,
      differences: differences.length > 0 ? differences : undefined
    };
  }

  // Private helper methods

  private splitIntoParagraphs(text: string): string[] {
    // Split on double line breaks (paragraph boundaries)
    // Single line breaks within paragraphs are preserved as spaces
    return text
      .split(/\n\s*\n/) // Split on double line breaks with optional whitespace
      .map(paragraph => 
        paragraph
          .replace(/\n/g, ' ') // Convert single line breaks to spaces
          .replace(/\s+/g, ' ') // Normalize multiple spaces
          .trim()
      )
      .filter(paragraph => paragraph.length > 0);
  }

  /**
   * Split text into paragraphs and capture the original separators
   */
  private splitIntoParagraphsWithSeparators(text: string): { paragraphs: string[], separators: string[] } {
    const separators: string[] = [];
    const paragraphs: string[] = [];
    
    // Find all paragraph separators (one or more newlines, possibly with whitespace)
    const separatorMatches = text.match(/\n\s*\n+/g) || [];
    
    // Split text by separators while preserving the separators
    const parts = text.split(/(\n\s*\n+)/);
    
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Even indices are paragraph content
        const paragraph = parts[i].trim();
        if (paragraph.length > 0) {
          paragraphs.push(paragraph);
        }
      } else {
        // Odd indices are separators
        separators.push(parts[i]);
      }
    }
    
    return { paragraphs, separators };
  }

  private determineBlockType(paragraph: string, options: ConversionOptions): BackendBlock['type'] {
    // Simple heuristics for block type detection
    // Can be enhanced based on document type and jurisdiction
    
    if (paragraph.match(/^(WHEREAS|NOW THEREFORE|IN WITNESS WHEREOF)/i)) {
      return 'Clause';
    }
    
    if (paragraph.match(/^(ARTICLE|SECTION|CHAPTER)\s+[IVX\d]/i)) {
      return 'Section';
    }
    
    if (paragraph.match(/^[A-Z\s]{10,}$/)) {
      return 'Heading';
    }
    
    // Default to Paragraph
    return 'Paragraph';
  }

  private determineStyleId(paragraph: string, options: ConversionOptions): string {
    // Determine style based on content and options
    if (options.documentType === 'contract' && paragraph.match(/^(WHEREAS|NOW THEREFORE)/i)) {
      return 'contract-clause';
    }
    
    if (paragraph.match(/^[A-Z\s]{10,}$/)) {
      return 'heading';
    }
    
    return 'Normal';  // Backend expects 'Normal', not 'default'
  }

  private determineCharStyleId(paragraph: string, options: ConversionOptions): string {
    // Character-level styling determination
    return 'default';
  }

  private generateBlockAttributes(paragraph: string, options: ConversionOptions): Record<string, any> {
    const attrs: Record<string, any> = {};
    
    // Add jurisdiction-specific attributes
    if (options.jurisdiction) {
      attrs.jurisdiction = options.jurisdiction;
    }
    
    // Add document type attributes
    if (options.documentType) {
      attrs.documentType = options.documentType;
    }
    
    return attrs;
  }

  private generateConversionWarnings(text: string, ast: BackendAST, options: ConversionOptions): string[] {
    const warnings: string[] = [];
    
    // Check for potential formatting loss
    if (text.includes('\t')) {
      warnings.push('Tab characters converted to spaces');
    }
    
    if (text.match(/\n{3,}/)) {
      warnings.push('Multiple consecutive line breaks normalized to paragraph breaks');
    }
    
    // Check for complex formatting that might be lost
    if (text.includes('**') || text.includes('__')) {
      warnings.push('Markdown-style formatting detected but not preserved in AST');
    }
    
    return warnings;
  }

  private generateReverseConversionWarnings(ast: BackendAST, text: string): string[] {
    const warnings: string[] = [];
    
    // Check for block types that might lose information
    const complexBlocks = ast.blocks.filter(block => 
      ['Table', 'Image', 'CodeBlock'].includes(block.type)
    );
    
    if (complexBlocks.length > 0) {
      warnings.push(`${complexBlocks.length} complex block(s) converted to plain text`);
    }
    
    // Check for attributes that are lost
    const blocksWithAttrs = ast.blocks.filter(block => 
      block.attrs && Object.keys(block.attrs).length > 0
    );
    
    if (blocksWithAttrs.length > 0) {
      warnings.push(`Block attributes lost in text conversion`);
    }
    
    return warnings;
  }

  private normalizeText(text: string): string {
    return text
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private calculateTextSimilarity(text1: string, text2: string): number {
    if (text1 === text2) return 1.0;
    if (text1.length === 0 && text2.length === 0) return 1.0;
    if (text1.length === 0 || text2.length === 0) return 0.0;
    
    // Simple character-based similarity
    const maxLength = Math.max(text1.length, text2.length);
    let matches = 0;
    
    for (let i = 0; i < Math.min(text1.length, text2.length); i++) {
      if (text1[i] === text2[i]) {
        matches++;
      }
    }
    
    return matches / maxLength;
  }

  private findTextDifferences(text1: string, text2: string): string[] {
    const differences: string[] = [];
    
    if (text1.length !== text2.length) {
      differences.push(`Length difference: ${text1.length} vs ${text2.length} characters`);
    }
    
    // Find first difference
    for (let i = 0; i < Math.min(text1.length, text2.length); i++) {
      if (text1[i] !== text2[i]) {
        const context = Math.max(0, i - 10);
        const contextEnd = Math.min(text1.length, i + 10);
        differences.push(`First difference at position ${i}: "${text1.substring(context, contextEnd)}" vs "${text2.substring(context, contextEnd)}"`);
        break;
      }
    }
    
    return differences;
  }

  /**
   * Get conversion statistics for monitoring
   */
  getConversionStats(text: string, ast: BackendAST): {
    textLength: number;
    paragraphCount: number;
    blockCount: number;
    runCount: number;
    averageBlockLength: number;
  } {
    const paragraphs = this.splitIntoParagraphs(text);
    const runCount = ast.blocks.reduce((total, block) => 
      total + (block.children?.length || 0), 0
    );
    
    return {
      textLength: text.length,
      paragraphCount: paragraphs.length,
      blockCount: ast.blocks.length,
      runCount,
      averageBlockLength: ast.blocks.length > 0 
        ? Math.round(text.length / ast.blocks.length)
        : 0
    };
  }
}
